<!--
  Presentational card for the Add / Edit Prompt Template dialogs.
  Extracted to remove duplicated markup (SonarQube) between the two near-identical
  dialogs in PromptTemplate.vue. Contains NO business logic: the parent still owns
  ModelData, the open/close dialog state, and the save/close handlers. This child
  only renders the shared markup and emits `save` / `close`. Behavior is unchanged;
  the per-mode differences (title, ids, placeholder, switch color, textarea maxlength,
  actions layout) are driven by the `mode` prop exactly as before.
-->
<template>
  <v-card class="dialog-card" :elevation="mode === 'edit' ? 8 : undefined">
    <!-- Header Section -->
    <div class="dialog-header" :class="mode === 'edit' ? 'dialog-header-edit' : 'dialog-header-add'">
      <div class="header-main">
        <div class="header-text-content">
          <div class="title-with-chip">
            <h2 class="dialog-title">
              {{ mode === 'edit' ? $t("edit") : $t("add") }} {{ $t("PromptTemplate") }}
            </h2>
            <v-chip size="small" variant="tonal" color="info" class="summary-chip"
              :class="[modelData.activeStatus ? 'chip-active' : 'chip-inactive']">
              <span v-if="modelData.activeStatus">{{ $t('Active') }}</span>
              <span v-else>{{ $t('Inactive') }}</span>
            </v-chip>
          </div>
          <p class="dialog-subtitle">
            {{ mode === 'edit' ? $t('_modifyExistingPromptTemplate') : $t('_createNewPromptTemplate') }}
          </p>
        </div>
      </div>
      <div class="header-actions">
        <v-btn icon variant="text" @click="$emit('close')" class="close-button">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <v-divider></v-divider>

    <!-- Content Section -->
    <v-card-text class="dialog-content">
      <v-form ref="promptForm" v-model="validForm" lazy-validation>
        <v-row>
          <!-- Name Field -->
          <v-col cols="12" md="8">
            <div class="field-container">
              <span class="field-label ml-0">
                {{ $t('templateName') }}
                <span style="color: red;">*</span>
                <v-tooltip location="top" max-width="300px">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="grey-darken-1" size="small">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ $t('_templateNameTooltip') }}</span>
                </v-tooltip>
              </span>
              <v-text-field :id="`name-field-popup${mode}-prompt`" v-model="modelData.name" :rules="[rules.required]"
                :placeholder="mode === 'edit' ? $t('_enterTemplateName') : $t('templateName')" variant="outlined"
                density="comfortable" clearable color="primary" class="modern-field" hide-details="auto">
              </v-text-field>
            </div>
          </v-col>

          <!-- Status Switch -->
          <v-col cols="12" md="4" class="mt-4">
            <div style="display: flex; align-items: center; justify-content: center;">
              <v-switch :id="`switch-status-popup${mode}-prompt`" v-model="modelData.activeStatus" inset
                :color="mode === 'edit' ? 'success' : undefined"
                :class="['publication-toggle', modelData.activeStatus ? 'publication-toggle--active' : 'publication-toggle--inactive']"
                hide-details>
              </v-switch>
            </div>
          </v-col>

          <!-- Prompt Textarea -->
          <v-col cols="12">
            <div class="field-container">
              <span class="field-label ml-0">
                {{ $t('PromptTemplate') }}
                <span style="color: red;">*</span>
                <v-tooltip location="top" max-width="300px">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="grey-darken-1" size="small">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ $t("PromptTemplate") }}</span>
                </v-tooltip>
              </span>
              <v-textarea :id="`prompt-field-popup${mode}-prompt`" v-model="modelData.prompt" :rules="[rules.required]"
                :placeholder="$t('_enterPromptTemplate')" variant="outlined" rows="6" auto-grow counter
                :maxlength="mode === 'edit' ? maxLengthChar : undefined" color="primary" class="modern-textarea"
                hide-details="auto">
              </v-textarea>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>

    <!-- Actions Section -->
    <v-card-actions class="dialog-actions">
      <template v-if="mode === 'edit'">
        <div class="actions-content">
          <div class="actions-left"></div>
          <div class="actions-right">
            <v-btn variant="outlined" color="grey" size="large" @click="$emit('close')"
              class="dialog-action-btn cancel-btn">
              {{ $t("cancel") }}
            </v-btn>
            <v-btn id="button-add-popupedit-prompt" variant="flat" size="large" @click="$emit('save')"
              class="dialog-action-btn save-btn-edit" :loading="loading">
              <v-img :src="images.saveImg" width="20" height="20" class="mr-2" />
              {{ $t('saveChanges') }}
            </v-btn>
          </div>
        </div>
      </template>
      <template v-else>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" color="grey" size="large" @click="$emit('close')" class="cancel-btn">
          {{ $t("cancel") }}
        </v-btn>
        <v-btn id="button-add-popupadd-prompt" variant="flat" size="large" @click="$emit('save')"
          class="dialog-action-btn save-btn-add" :loading="loading">
          <v-img :src="images.saveImg" width="20" height="20" class="mr-2" />
          {{ $t("save") }}
        </v-btn>
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "PromptTemplateDialogCard",
  props: {
    mode: { type: String, required: true }, // "add" | "edit"
    modelValue: { type: Object, required: true },
    rules: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    images: { type: Object, required: true },
    maxLengthChar: { type: [Number, String], default: undefined },
  },
  emits: ["save", "close", "update:modelValue"],
  data() {
    return {
      validForm: false,
      // Local editable copy of modelValue; synced back to the parent via v-model (update:modelValue)
      // so template v-models never mutate the prop directly.
      modelData: JSON.parse(JSON.stringify(this.modelValue || {})),
    };
  },
  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.modelData)) {
          this.modelData = JSON.parse(JSON.stringify(val || {}));
        }
      },
    },
    modelData: {
      deep: true,
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.modelValue)) {
          this.$emit("update:modelValue", JSON.parse(JSON.stringify(val)));
        }
      },
    },
  },
};
</script>
